// 에러 타입 정의
interface CustomError extends Error {
    response?: {
        status?: number;
        data?: {
            message?: string;
            error?: {
                errors: {
                    message: string;
                }[];
            };
        };
    };
}

// api 에러메시지
export function errorMsgHandler(error: CustomError): string {
    let errorMsg = "";
    let errorType = "";

    if (error.message) {
        errorType = "javascript-api";
    }

    if (error.response && error.response.data) {
        // 일반 에러 객체 처리
        errorType = "custom";
    }

    if (error.response && error.response.data && error.response.data.error) {
        // validator 에러 객체 처리
        errorType = "express-validator";
    }

    if (errorType === "javascript-api") {
        // 일반 에러 객체 처리
        errorMsg = error.message;
    } else if (errorType === "custom") {
        // 일반 에러 객체 처리
        errorMsg = error.response!.data!.message!;
    } else if (errorType === "express-validator") {
        // validator 에러 객체 처리
        errorMsg = error.response!.data!.error!.errors[0].message;
    } else {
        errorMsg = "알 수 없는 오류가 발생했습니다.";
    }

    if (!errorMsg) {
        const status = error.response?.status;
        errorMsg = status
            ? `요청 처리에 실패했습니다. (status: ${status})`
            : "요청 처리에 실패했습니다.";
    }

    return errorMsg;
}

// 숫자형 콤마넣기
export function makeIntComma(intVal: number): string {
    intVal *= 1; // 숫자형 변경
    const option = {
        maximumFractionDigits: 4, // 최대 소수점 자리
    };
    const result = intVal.toLocaleString("ko-KR", option);
    return result;
}

export const regPhone = /^\d{3}-\d{4}-\d{4}$/;
export const regEmail = /^[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*@[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*\.[a-zA-Z]{2,3}$/i;
export const regPassword = /^(?=.*[a-zA-Z])(?=.*\d)[a-zA-Z\d]{6,}$/;

// 08시 40분 까지 -> 오전 8시 40분 까지
export const formatDeliveryTime = (time: string) => {
    const parts = time.split(" "); // ["08시", "40분", "까지"]
    // "오전중", "오후중", "당일배송" 등 특수 배송시간은 그대로 반환
    if (parts.length < 3) return time;

    const [hour, minute, type] = parts;
    const intHour = parseInt(hour);
    let formattedHour = "";

    if (intHour >= 12) {
        formattedHour = "오후 " + (intHour === 12 ? 12 : intHour - 12) + "시";
    } else {
        formattedHour = "오전 " + intHour + "시";
    }

    return formattedHour + " " + minute + " " + type;
};

// 2025-03-07 12:22:48.000 -> 오후 12시 22분
export const formatDeliveryTimeFromDate = (date: string) => {
    const time = new Date(date);
    const hour = time.getHours();
    const minute = time.getMinutes();
    let formattedHour = "";

    if (hour >= 12) {
        formattedHour = "오후 " + (hour === 12 ? 12 : hour - 12) + "시";
    } else {
        formattedHour = "오전 " + (hour === 0 ? 12 : hour) + "시";
    }

    return formattedHour + " " + minute + "분";
};

// Date -> 2025-03-07
export const formatDeliveryDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // 월은 0부터 시작하므로 +1
    const day = String(date.getDate()).padStart(2, "0");

    // yyyy-MM-dd 형식으로 변환
    const formattedDate = `${year}-${month}-${day}`;

    return formattedDate;
};

// 2025.03.07(월) -> Date
export const formatDisplayDateStringToDate = (dateString: string) => {
    const formatDateString = dateString.replace(/\./g, "-").replace(/\(.*\)/, "") + "T00:00:00";
    const newDate = new Date(formatDateString);
    return newDate;
};

// 2025.03.07(월) -> 2025.03.07(월)
// 2025-03-07 -> 2025.03.07(월)
export const formatDisplayDeliveryDate = (date: string) => {
    // 1. 신규주문시: 빈값
    // 2. 날짜 선택시: 0000-00-00

    // 1. 주문수정시: 0000.00.00(0)
    // 2. 날짜 선택시: 0000-00-00

    if (!date) return ""; // 신규 주문시 초기값 빈 값

    if (date.includes(".")) return date; // 주문 수정시 초기값

    const formatDateString = date + "T00:00:00";
    const newDate = new Date(formatDateString);
    const year = newDate.getFullYear();
    const month = String(newDate.getMonth() + 1).padStart(2, "0"); // 월은 0부터 시작하므로 +1
    const day = String(newDate.getDate()).padStart(2, "0");

    // 요일
    const daysOfWeek = ["일", "월", "화", "수", "목", "금", "토"];
    const dayOfWeek = daysOfWeek[newDate.getDay()]; // 0: 일요일, 1: 월요일, ..., 6: 토요일

    // yyyy.MM.dd(eeeeee) 형식으로 변환
    const formattedDate = `${year}.${month}.${day}(${dayOfWeek})`;

    return formattedDate;
};

// 오후 3시, 20분 -> 15시 20분
export const format12To24 = (hour: string, minute: string) => {
    let hourValue = hour;
    let formattedHour;

    // 12시간 형식에서 24시간 형식으로 변환
    if (hourValue.includes("오후")) {
        const cleanHour = hourValue.replace("오후 ", "").replace("시", "");
        formattedHour = parseInt(cleanHour) === 12 ? "12" : (parseInt(cleanHour) + 12).toString(); // 오후일 경우 12를 더함
    } else {
        const cleanHour = hourValue.replace("오전 ", "").replace("시", "");
        formattedHour = cleanHour.length === 1 ? "0" + cleanHour : cleanHour;
    }
    const time = formattedHour + "시" + " " + minute;
    return time;
};

// 사번 포맷
export const returnCompanyTelFormat = (value: string) => {
    let companyTelFormat;

    switch (true) {
        case value.startsWith("15"):
            companyTelFormat = "####-####";
            break;
        case value.startsWith("010"):
            companyTelFormat = "###-####-####";
            break;
        case value.startsWith("02"):
            companyTelFormat = "##-####-####";
            break;
        case value.startsWith("070"):
            companyTelFormat = "###-####-####";
            break;
        default:
            companyTelFormat = "###-###-####";
            break;
    }
    return companyTelFormat;
};
